import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ScrollReveal from '@/components/ScrollReveal';
import {
  Users,
  Landmark,
  Pill,
  Radio,
  Building,
  Factory,
  CheckCircle,
  ArrowRight,
  Shield,
  TrendingUp,
  Lock,
  BarChart3,
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const industries = [
  {
    id: 'recruitment',
    icon: Users,
    title: 'Recruitment',
    tagline: 'AI workflows for modern talent acquisition',
    description:
      'Transform recruitment operations with AI-powered workflows that reduce manual screening time while maintaining quality and compliance with hiring policies.',
    image: '/images/industries/recruitment.jpg',
    solutions: [
      {
        title: 'CV ingestion and normalisation',
        desc: 'Automatically process incoming CVs, extract structured data, and normalise formats for consistent evaluation',
      },
      {
        title: 'Intelligent screening and ranking',
        desc: 'AI-powered candidate ranking based on role requirements, with explainable scoring and bias detection',
      },
      {
        title: 'Interview preparation assistants',
        desc: 'Generate role-specific interview questions and talking points based on candidate profiles',
      },
      {
        title: 'Policy Q&A and compliance',
        desc: 'Instant access to hiring policies, employment law guidance, and compliance requirements',
      },
    ],
    outcomes: [
      'Reduce initial screening time by 60-80%',
      'Improve candidate quality through consistent evaluation criteria',
      'Ensure compliance with hiring policies and regulations',
      'Free recruiters to focus on relationship-building and strategic sourcing',
    ],
  },
  {
    id: 'fintech',
    icon: Landmark,
    title: 'FinTech',
    tagline: 'Compliance and intelligence for financial services',
    description:
      'AI systems designed for the stringent requirements of financial services: auditability, traceability, and regulatory compliance built in from day one.',
    image: '/images/industries/fintech.jpg',
    solutions: [
      {
        title: 'Document intelligence and extraction',
        desc: 'Automated processing of financial documents, contracts, and regulatory filings with high accuracy',
      },
      {
        title: 'Policy-driven compliance checks',
        desc: 'Validate transactions and operations against regulatory rules with full audit trails',
      },
      {
        title: 'Evidence compilation and reporting',
        desc: 'Automated regulatory reporting and audit evidence generation',
      },
      {
        title: 'Risk dashboards and executive summaries',
        desc: 'Board-ready risk reporting with drill-down to source evidence',
      },
    ],
    compliance: [
      'Complete audit trails from data ingestion to output',
      'Source grounding and citation for all AI-generated content',
      'Role-based access controls aligned with financial data governance',
      'Evaluation frameworks for continuous compliance monitoring',
      'Explainability and transparency in decision-making processes',
    ],
  },
  {
    id: 'pharma',
    icon: Pill,
    title: 'Pharma',
    tagline: 'Knowledge and compliance AI for pharmaceutical operations',
    description:
      'Support pharmaceutical teams with AI-powered access to regulatory documentation, research synthesis, and quality management workflows — all built with GMP compliance and validation requirements in mind.',
    image: '/images/industries/pharma.jpg',
    solutions: [
      {
        title: 'Regulatory documentation assistants',
        desc: 'AI-powered search and synthesis across regulatory guidelines, SOPs, and quality documentation',
      },
      {
        title: 'Research synthesis',
        desc: 'Controlled access to research documents with role-based permissions and data governance',
      },
      {
        title: 'SOP and policy assistants',
        desc: 'Instant access to standard operating procedures with version control and change tracking',
      },
      {
        title: 'Audit-ready workflows',
        desc: 'Full traceability from query to source documents for regulatory inspections',
      },
    ],
    compliance: [
      'GMP-compliant system design and validation support',
      '21 CFR Part 11 compliance for electronic records',
      'Change control and version management',
      'User access controls and authentication',
      'Complete audit trails and data integrity',
    ],
  },
  {
    id: 'platform-engineering',
    icon: Radio,
    title: 'IT Platform Engineering & DX',
    tagline: 'Developer experience tools for Kubernetes, ARM64 migration, and cloud-native infrastructure',
    description:
      'Production-ready platform engineering solutions that accelerate developer workflows, reduce infrastructure complexity, and deliver enterprise-grade Kubernetes experiences.',
    image: '/images/industries/telecom.jpg',
    solutions: [
      {
        title: 'EKS-D-Express',
        desc: 'Fully open source AWS Community project, EKS compatible distribution, that allows you to spin up multiple clusters in less than 3 minutes, with EKS Pod Identity layer and full Karpenter integration. If you are searching for GKE or AKS free tier equivalent on AWS, we\'ve built one.',
      },
      {
        title: 'Express-Compute Pro',
        desc: 'Transparent adaptive autoscaling for modern workloads on Kubernetes, with AI assistance guidance and low level monitoring, in partnership with Memverge and Plasticity.Cloud, for your Kubernetes Agentic workloads. With transparent migration of applications between hosts, fully utilizing spot capacity of cloud providers.',
      },
      {
        title: 'ARM64 CoDriver',
        desc: 'Full application suite that allows you to migrate Java workloads from x86-64 to arm64, in a staged, progressive way, with options to perform migration on premises or in the cloud, for regulated industries and beyond. We worked with Java workloads on arm64 since AWS announced first generation of Graviton — you can\'t ignore such expertise.',
      },
      {
        title: 'KubeCoDriver',
        desc: 'Take full advantage of ephemeral containers, with fully auditable actions, RBAC for users and applications, targeting continuous monitoring and testing and chaos engineering.',
      },
    ],
    outcomes: [
      'Spin up production-ready Kubernetes clusters in under 3 minutes',
      'Reduce cloud costs through intelligent spot capacity utilization',
      'Seamless Java workload migration from x86-64 to ARM64',
      'Full audit trail and RBAC for all container operations',
    ],
  },
  {
    id: 'government',
    icon: Building,
    title: 'Government',
    tagline: 'Secure AI for public sector operations',
    description:
      'Secure, compliant AI solutions designed for government and public sector requirements.',
    image: '/images/government.jpg',
    solutions: [
      {
        title: 'Citizen service automation',
        desc: 'AI-powered assistance for citizen inquiries and service requests',
      },
      {
        title: 'Document processing',
        desc: 'Automated processing of government forms and applications',
      },
      {
        title: 'Policy analysis',
        desc: 'AI-assisted policy research and impact analysis',
      },
      {
        title: 'Secure communications',
        desc: 'Encrypted, auditable AI systems for sensitive operations',
      },
    ],
    features: [
      'Air-gapped deployment options for maximum security',
      'Full audit trails and compliance reporting',
      'Role-based access aligned with government security classifications',
      'Integration with existing government IT infrastructure',
    ],
  },
  {
    id: 'manufacturing',
    icon: Factory,
    title: 'Manufacturing & Industry 4.0',
    tagline: 'Cognitive Industrial Edge for agentic manufacturing intelligence',
    description:
      'Transform factory operations with autonomous AI agents deployed within secure, air-gapped environments. Our Cognitive Industrial Edge architecture enables machinery to reason about its state, coordinate multi-machine workflows, and autonomously optimise production.',
    image: '/images/industries/manufacturing.jpg',
    solutions: [
      {
        title: 'Agentic Production Optimisation',
        desc: 'AI agents that perceive environments, plan sequences, and execute actions using a Supervisor-Worker pattern',
      },
      {
        title: 'Protocol Abstraction Layer',
        desc: 'Unified connectivity across OPC UA, Modbus, MQTT, PROFINET, and EtherNet/IP protocols',
      },
      {
        title: 'Edge AI Deployment',
        desc: 'SLMs and quantised LLMs on NVIDIA/AMD edge hardware for real-time factory floor intelligence',
      },
      {
        title: 'ERP Integration',
        desc: 'Northbound integration with SAP, Oracle, Dynamics 365, and Epicor with shadow inventory management',
      },
    ],
    expertise: [
      'Air-gapped "Fortress Factory" security architecture',
      'Functional safety integration with strict AI/Safety PLC separation',
      'Visual inspection agents for quality yield improvement',
      'Predictive maintenance for 10-20% OEE improvement',
      'Phased rollout: Silent Observer → Co-Pilot → Bounded Autonomy',
    ],
    link: '/manufacturing',
  },
];

const approachItems = [
  {
    icon: Shield,
    title: 'Security first',
    desc: 'Every system designed with data governance, access controls, and encryption from the start',
  },
  {
    icon: Lock,
    title: 'Governance built-in',
    desc: 'Audit trails, source grounding, and policy controls as core features, not add-ons',
  },
  {
    icon: TrendingUp,
    title: 'Production-ready',
    desc: 'Systems designed for operational scale, reliability, and enterprise SLAs',
  },
  {
    icon: BarChart3,
    title: 'Measurable ROI',
    desc: 'Focus on concrete business outcomes: cycle time, cost reduction, quality improvement',
  },
];

export default function IndustriesPage() {
  useEffect(() => {
    ScrollTrigger.refresh();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Page Header */}
        <section className="pt-44 pb-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
          <div className="container-custom relative z-10">
            <ScrollReveal className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-6">
                Industries
              </h1>
              <p className="text-lg sm:text-xl text-slate-600">
                Deep expertise across regulated and mission-critical sectors
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* Industries Content */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="space-y-32">
              {industries.map((industry, index) => (
                <div key={industry.id} id={industry.id}>
                  <div
                    className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                      index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                    }`}
                  >
                    {/* Image */}
                    <ScrollReveal
                      direction={index % 2 === 0 ? 'left' : 'right'}
                      className={index % 2 === 1 ? 'lg:order-2' : ''}
                    >
                      <div className="relative rounded-xl overflow-hidden group shadow-card">
                        <img
                          src={industry.image}
                          alt={industry.title}
                          className="w-full h-96 object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />
                        <div className="absolute bottom-6 left-6 w-16 h-16 bg-primary rounded-xl flex items-center justify-center shadow-lg">
                          <industry.icon className="w-8 h-8 text-white" />
                        </div>
                      </div>
                    </ScrollReveal>

                    {/* Content */}
                    <ScrollReveal
                      direction={index % 2 === 0 ? 'right' : 'left'}
                      delay={0.2}
                      className={index % 2 === 1 ? 'lg:order-1' : ''}
                    >
                      <div>
                        <span className="text-primary text-sm font-medium uppercase tracking-wider">
                          {industry.tagline}
                        </span>
                        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-2 mb-4">
                          {industry.title}
                        </h2>
                        <p className="text-slate-600 leading-relaxed mb-8">
                          {industry.description}
                        </p>

                        {/* Solutions */}
                        <div className="mb-8">
                          <h4 className="text-slate-900 font-semibold mb-4">
                            Solutions
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {industry.solutions.map((solution, i) => (
                              <div
                                key={i}
                                className="bg-slate-50 rounded-lg p-4 border border-slate-200"
                              >
                                <h5 className="text-slate-900 font-medium text-sm mb-1">
                                  {solution.title}
                                </h5>
                                <p className="text-slate-500 text-xs">
                                  {solution.desc}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Outcomes / Compliance / Features */}
                        {(industry.outcomes || industry.compliance || industry.features || industry.expertise) && (
                          <div className="mb-8">
                            <h4 className="text-slate-900 font-semibold mb-4">
                              {industry.outcomes
                                ? 'Typical Outcomes'
                                : industry.compliance
                                ? 'Regulatory Compliance Focus'
                                : industry.features
                                ? 'Key Features'
                                : 'Manufacturing Expertise'}
                            </h4>
                            <ul className="space-y-2">
                              {(industry.outcomes || industry.compliance || industry.features || industry.expertise || []).map(
                                (item, i) => (
                                  <li
                                    key={i}
                                    className="flex items-start gap-2 text-slate-600 text-sm"
                                  >
                                    <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                                    {item}
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        )}

                        {/* Link to Manufacturing Page */}
                        {industry.link && (
                          <Link
                            to={industry.link}
                            className="inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all"
                          >
                            Explore our Cognitive Industrial Edge architecture
                            <ArrowRight className="w-4 h-4" />
                          </Link>
                        )}
                      </div>
                    </ScrollReveal>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cross-Industry Approach */}
        <section className="section-padding bg-slate-50">
          <div className="container-custom">
            <ScrollReveal className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Our Cross-Industry Approach
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                While we have deep expertise in specific sectors, our approach to
                AI is consistent across all industries
              </p>
            </ScrollReveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {approachItems.map((item, index) => (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-card text-center group hover:-translate-y-2 transition-transform duration-300">
                    <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                      <item.icon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="text-slate-900 font-semibold mb-2">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 text-sm">{item.desc}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding">
          <div className="container-custom">
            <ScrollReveal className="text-center max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Discuss your industry-specific AI requirements
              </h2>
              <p className="text-slate-600 mb-8">
                Book a discovery call to explore how AI can create value in your
                sector.
              </p>
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2 group"
              >
                Book a Discovery Call
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </ScrollReveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
